/**
 * Create Actions - Snippet creation with UI integration
 *
 * This action handles snippet creation (and duplication) with full UI integration:
 * - Creates the snippet via the API
 * - Shows success/error flags
 * - Adds the snippet to the editor's quick insert menu
 * - Refreshes the snippets list
 *
 * Both CreateSnippetModal and DuplicateSnippetModal call createSnippet, each
 * passing their own i18n messages via options.messages.
 */
import type { StoreActionApi } from 'react-sweet-state';

import type { ADFEntity } from '@atlaskit/adf-utils/types';

import type { SnippetCreateFailureReason } from '../../constants/snippetAnalytics.ts';
import { createSnippetModalMessages } from '../../modals/CreateSnippetModal.i18n.ts';
import type { Snippet } from '../../types/snippet.ts';
import type { SnippetsState } from '../snippetsStore.types.ts';
import { messages as baseMessages } from '../../modals/base/SnippetBaseModal.i18n.ts';
import { reportError } from '../../utils/reportError.ts';
import * as BlockServiceClient from '../../services/blockServiceClient.ts';
import { clearSnippetsCache } from '../../services/cache/snippetsCache.ts';
import { classifyCreateError } from '../../services/types/blockServiceTypes.ts';
import { showSnippetErrorFlag, showSnippetSuccessFlag } from '../../utils/snippetFlags.ts';

import {
	addSnippetToQuickInsertAndShare,
	buildCreatePayload,
	getSnippetErrorFlagMessages,
	type CreateSnippetOptions,
} from './actionsHelper.ts';

/** Structured, PII-safe info about a create failure, for the analytics layer. */
export type CreateErrorInfo = {
	reason: SnippetCreateFailureReason;
	statusCode?: number;
};

// Module-level create-failure callback, registered by the React analytics layer
// (mirrors fetchActions.ts) so the store stays free of React/analytics deps.
let onCreateErrorCallback: ((info: CreateErrorInfo) => void) | null = null;

/** Registers a callback fired when a create fails. */
export const setOnCreateError = (callback: ((info: CreateErrorInfo) => void) | null): void => {
	onCreateErrorCallback = callback;
};

const invokeCreateErrorCallback = (info: CreateErrorInfo): void => {
	onCreateErrorCallback?.(info);
};

/**
 * Actions for creating snippets with UI integration.
 */
export const createActions = {
	/**
	 * Creates a new snippet and refreshes the snippets list.
	 *
	 * After successful creation:
	 * - Clears the service cache and fetches the updated list
	 * - Shows a success flag (if options provided)
	 * - Adds the snippet to the quick insert menu
	 *
	 * On error:
	 * - Reports to monitoring
	 * - Shows an error flag (if options provided)
	 * - Keeps existing snippets visible (doesn't reset isLoaded)
	 *
	 * @param name - The snippet name (required)
	 * @param body - The ADF content body
	 * @param description - Optional description text
	 * @param options - UI options including flags, intl, editor API ref, and i18n messages
	 * @returns Promise resolving to the newly created snippet, or null on failure
	 */
	createSnippet:
		(name: string, body: ADFEntity, description?: string, options?: CreateSnippetOptions) =>
		async (storeApi: StoreActionApi<SnippetsState>): Promise<Snippet | null> => {
			const { getState, setState } = storeApi;
			const messages = options?.messages ?? createSnippetModalMessages;
			const params = buildCreatePayload(name, body, description);

			setState({ isLoading: true });

			try {
				const snippet = await BlockServiceClient.createSnippet(params);
				const { isSnippetSharingForSiteEnabled, snippetsLocale } = getState();

				clearSnippetsCache();
				const updatedSnippets = await BlockServiceClient.fetchSnippets({
					isSnippetSharingForSiteEnabled,
					locale: snippetsLocale,
				});

				setState({
					snippets: updatedSnippets,
					isLoading: false,
					isLoaded: true,
				});

				if (options) {
					const {
						flags,
						intl,
						editorAPI,
						isShared,
						onShareFailure,
						successFlagActions,
						showSuccessFlag = true,
					} = options;
					if (showSuccessFlag) {
						showSnippetSuccessFlag(
							flags,
							intl.formatMessage(messages.successFlagTitle, { snippetName: snippet.name }),
							intl.formatMessage(baseMessages.successFlagDescription),
							successFlagActions,
						);
					}
					await addSnippetToQuickInsertAndShare({
						snippet,
						options: { flags, intl, editorAPI, isShared },
						storeApi,
						onShareFailure,
					});
				}
				return snippet;
			} catch (thrownError) {
				const err = thrownError instanceof Error ? thrownError : new Error(String(thrownError));

				setState({ isLoading: false });
				reportError(err);

				// Surface a PII-safe, classified failure to the analytics layer
				// (gating/firing handled by useSnippetsCreateAnalytics).
				invokeCreateErrorCallback(classifyCreateError(err));

				if (options) {
					options.onSaveFailure?.(err);
					const { flagTitle, flagDescription } = getSnippetErrorFlagMessages(
						err,
						messages.errorFlagTitle,
						messages.errorFlagDescription,
					);
					showSnippetErrorFlag(
						options.flags,
						options.intl.formatMessage(flagTitle),
						options.intl.formatMessage(flagDescription),
					);
					return null;
				}
				throw err;
			}
		},
};
